import React, { useEffect, useState } from 'react';
import Card from '../../../components/card/Card';
import TextArea from '../../../components/textarea/TextArea';
import Button from '../../../components/button/Button';
import P from '../../../components/texts/p/P';
import Spinner from '../../../components/Spinner/spinner';
import type { applicationInfo, clientData } from '../../../types/interfaces';
import { sendApplication } from '../../../services/ApplicationService';

type AtlanticMotivoFieldProps = {
  cuit: string;
  applicationId: string;
  clientData: clientData;
  initialReason?: string | null;
  editable?: boolean;
  onSaved?: (reason: string) => void;
};

function buildApplicationPayload(
  client: clientData,
  reason: string
): applicationInfo {
  return {
    products: client.products ?? [],
    manual_total_amount: !!client.manual_total_amount,
    requested_amount: client.requested_amount ?? [],
    reason,
    review: client.review ?? '',
    extra_comments: client.extra_comments ?? '',
    has_valid_corporate_composition: !!client.has_valid_corporate_composition,
    signers: client.signers ?? [],
    category_type_id: client.category_type_id,
    operation_type_id: client.operation_type_id,
    current_sgr_balance: client.current_sgr_balance,
    is_syndicated: !!client.is_syndicated,
  };
}

/**
 * Editable Motivo for Atlantic Paso 4. Persists via existing PUT sendApplication.
 */
const AtlanticMotivoField: React.FC<AtlanticMotivoFieldProps> = ({
  cuit,
  applicationId,
  clientData,
  initialReason,
  editable = true,
  onSaved,
}) => {
  const [reason, setReason] = useState(initialReason ?? clientData.reason ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setReason(initialReason ?? clientData.reason ?? '');
  }, [initialReason, clientData.reason]);

  const persist = async () => {
    if (!editable || !cuit || !applicationId) return;
    const trimmed = reason.trim();
    if (trimmed === (clientData.reason ?? '').trim()) return;

    setIsSaving(true);
    setError(null);
    try {
      const response = await sendApplication(
        cuit,
        buildApplicationPayload(clientData, trimmed),
        applicationId
      );
      if (response?.success === false || (response?.status && response.status >= 400)) {
        setError('No se pudo guardar el motivo');
        return;
      }
      onSaved?.(trimmed);
    } catch {
      setError('No se pudo guardar el motivo');
    } finally {
      setIsSaving(false);
    }
  };

  if (!editable) {
    return (
      <div data-testid="atlantic-motivo-readonly">
        <Card display="flex" gap="4px">
          <P type="2" variant="neutral" weight="bold" withMargin={false}>
            Motivo:
          </P>
          <P type="2" variant="neutral" withMargin={false}>
            {reason || '-'}
          </P>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="atlantic-motivo-field" style={{ width: '100%' }}>
      <TextArea
        id="atlantic-motivo-input"
        label="Motivo de la Solicitud:"
        placeHolder="Ingresar el motivo de la solicitud..."
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        onBlur={() => {
          void persist();
        }}
        disabled={isSaving}
        maxLengthHelpText={false}
        rows={3}
      />
      <Card margin={{ top: '2' }} display="flex" gap="8px" alignItems="center">
        <Button
          size="md"
          variant="secondary"
          onClick={() => {
            void persist();
          }}
          disabled={isSaving}
        >
          {isSaving ? <Spinner size="sm" variant="primary" /> : 'Guardar motivo'}
        </Button>
        {error && (
          <P type="3" variant="danger" withMargin={false}>
            {error}
          </P>
        )}
      </Card>
    </div>
  );
};

export default AtlanticMotivoField;
export { buildApplicationPayload };
